import React, { useState } from 'react';
import { db } from '../../../../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import {
    ChevronRight, ListOrdered, RotateCcw, Trophy, Check, Loader2
} from 'lucide-react';
import { StudentOMRModel, ScheduleEvent } from '../../../../../models';

interface ScheduleBookOMRProps {
    omrSets: any[];
    event: ScheduleEvent;
}

const ScheduleBookOMR: React.FC<ScheduleBookOMRProps> = ({ omrSets, event }) => {
    const [solvingSetId, setSolvingSetId] = useState<string | null>(null);
    const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastResult, setLastResult] = useState<any>(null);

    return (
        <div className="space-y-4">
            {!solvingSetId ? (
                omrSets.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {omrSets.map((set, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    setSolvingSetId(set.id);
                                    setUserAnswers({});
                                    setLastResult(null);
                                }}
                                className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-emerald-200 hover:bg-white transition-all text-left group"
                            >
                                <div className="flex flex-col">
                                    <h5 className="text-xs font-black text-slate-800">{set.title}</h5>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{set.questions.length} 문항</span>
                                </div>
                                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-emerald-500 group-hover:bg-emerald-600 group-hover:text-white transition-all">
                                    <ChevronRight size={16} />
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="py-12 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                        <ListOrdered size={32} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-xs font-bold text-slate-400">등록된 OMR 정보가 없습니다.</p>
                    </div>
                )
            ) : (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setSolvingSetId(null)}
                            className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <RotateCcw size={12} /> 목록으로 돌아가기
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-800">{omrSets.find(s => s.id === solvingSetId)?.title}</span>
                        </div>
                    </div>

                    <div className="space-y-1.5 mb-6">
                        {omrSets.find(s => s.id === solvingSetId)?.questions.map((q: any, qIdx: number) => (
                            <div key={qIdx} className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 hover:border-indigo-100 transition-all group">
                                <div className="w-6 h-6 shrink-0 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-[9px] font-black text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all">
                                    {q.number}
                                </div>
                                <div className="flex-1">
                                    {q.msType === 'short' ? (
                                        <input
                                            type="text"
                                            placeholder="정답 입력..."
                                            value={userAnswers[q.number] || ''}
                                            onChange={e => setUserAnswers({ ...userAnswers, [q.number]: e.target.value })}
                                            disabled={!!lastResult}
                                            className="w-full bg-slate-50 border-none rounded-lg px-2.5 py-1.5 text-xs font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all disabled:bg-white disabled:text-indigo-600"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
                                            {[1, 2, 3, 4, 5].map(opt => (
                                                <button
                                                    key={opt}
                                                    disabled={!!lastResult}
                                                    onClick={() => setUserAnswers({ ...userAnswers, [q.number]: opt.toString() })}
                                                    className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${userAnswers[q.number] === opt.toString()
                                                        ? 'bg-indigo-600 text-white shadow-md'
                                                        : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                                        }`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {lastResult && (
                                    <div className="flex items-center gap-2 min-w-[110px] justify-end">
                                        <div className={`px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest ${userAnswers[q.number] === q.answer ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
                                            {userAnswers[q.number] === q.answer ? '정답' : '오답'}
                                        </div>
                                        {userAnswers[q.number] !== q.answer && (
                                            <div className="text-[9px] font-bold text-slate-400 whitespace-nowrap">
                                                정답: {q.answer}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {!lastResult ? (
                        <button
                            onClick={async () => {
                                const activeSet = omrSets.find(s => s.id === solvingSetId);
                                if (!activeSet) return;
                                setIsSubmitting(true);
                                try {
                                    let correct = 0;
                                    activeSet.questions.forEach((q: any) => { if (userAnswers[q.number] === q.answer) correct++; });
                                    const result: StudentOMRModel = {
                                        id: '',
                                        memberId: event.memberId,
                                        branchId: event.branchId || undefined,
                                        bookId: event.bookId,
                                        bookTitle: event.bookTitle,
                                        subject: event.subject,
                                        setId: solvingSetId!,
                                        setTitle: activeSet.title,
                                        userAnswers,
                                        questions: activeSet.questions,
                                        correctCount: correct,
                                        totalCount: activeSet.questions.length,
                                        score: Math.round((correct / activeSet.questions.length) * 100),
                                        completedAt: Date.now()
                                    };
                                    // Remove undefined fields
                                    const { id, ...data } = result;
                                    const sanitizedData = Object.fromEntries(
                                        Object.entries(data).filter(([_, v]) => v !== undefined)
                                    );

                                    await addDoc(collection(db, 'studentOMR'), sanitizedData);
                                    setLastResult(result);
                                    alert(`채점이 완료되었습니다! 점수: ${result.score}점`);
                                } catch (err) {
                                    alert("제출 중 오류가 발생했습니다.");
                                } finally { setIsSubmitting(false); }
                            }}
                            disabled={isSubmitting}
                            className="w-full py-4 bg-indigo-600 text-white rounded-2xl text-xs sm:text-sm font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                        >
                            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            답안 제출 및 채점하기
                        </button>
                    ) : (
                        <div className="bg-slate-900 rounded-3xl p-6 text-center text-white">
                            <div className="w-16 h-16 bg-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl"><Trophy size={28} /></div>
                            <h4 className="text-lg font-black mb-0.5">학습 결과</h4>
                            <p className="text-slate-400 font-bold mb-4 italic text-xs">{lastResult.correctCount} / {lastResult.totalCount}</p>
                            <div className="flex items-center justify-center gap-4">
                                <div className="bg-white/10 px-5 py-3 rounded-2xl">
                                    <span className="block text-[9px] uppercase font-black text-slate-400 mb-0.5">최종 점수</span>
                                    <span className="text-2xl font-black">{lastResult.score}점</span>
                                </div>
                            </div>
                            <button onClick={() => setSolvingSetId(null)} className="mt-6 text-xs font-black text-indigo-400 hover:text-indigo-300 transition-all underline underline-offset-4">목록으로 나가기</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ScheduleBookOMR;
